import { Button, cn } from '@portal/ui';
import { apiFetch } from '@portal/web-sdk';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { NotificationItem } from '../contracts.js';
import { notificationKeys, useRelativeTime } from './queries.js';

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | 'all') =>
      apiFetch(id === 'all' ? 'notifications/read-all' : `notifications/${id}/read`, {
        method: 'POST',
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

/** Inbox rows: unread ones are highlighted; opening one marks it read and follows its link. */
export function NotificationItems({
  items,
  onOpen,
}: {
  items: NotificationItem[];
  onOpen?: () => void;
}) {
  const { t } = useTranslation('core-notification');
  const relative = useRelativeTime();
  const navigate = useNavigate();
  const markRead = useMarkRead();

  const open = (item: NotificationItem) => {
    if (!item.readAt) markRead.mutate(item.id);
    if (item.link) {
      onOpen?.();
      void navigate({ to: item.link });
    }
  };

  return (
    <ul className="divide-y">
      {items.map((item) => (
        <li
          key={item.id}
          className={cn('group flex gap-3 px-3 py-2.5', !item.readAt && 'bg-selection/60')}
        >
          <span
            aria-hidden
            className={cn(
              'mt-1.5 size-2 shrink-0 rounded-full',
              item.readAt ? 'bg-transparent' : 'bg-primary',
            )}
          />
          <button type="button" className="min-w-0 flex-1 text-left" onClick={() => open(item)}>
            <div className={cn('text-sm', !item.readAt && 'font-medium')}>{item.title}</div>
            <div className="line-clamp-3 text-sm whitespace-pre-line text-muted-foreground">
              {item.body}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">{relative(item.createdAt)}</div>
          </button>
          {!item.readAt ? (
            <Button
              variant="ghost"
              size="icon-sm"
              className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
              aria-label={t('markRead')}
              onClick={() => markRead.mutate(item.id)}
            >
              <Check />
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
